/**
 * Explanations for the intensity techniques the program prescribes.
 *
 * The wording is taken from The Hypertrophy Handbook (the companion PDF in
 * `examples/`), lightly tidied for reading on a phone — the source is the
 * author's, not ours, so it is transcribed rather than paraphrased.
 *
 * Kept on the client: it is fixed reference text, identical for every user,
 * with nothing to store or compare.
 */
export interface Technique {
  id: string;
  title: string;
  /** One line, shown before the detail. */
  summary: string;
  /** Paragraphs of explanation. */
  body: string[];
  /** An ordered worked example, when the handbook gives one. */
  steps?: string[];
  /** A caveat the handbook calls out explicitly. */
  note?: string;
}

export const TECHNIQUES: Technique[] = [
  {
    id: 'long-length-partials',
    title: 'Long-length Partials',
    summary: 'Partial reps in the stretched part of the lift.',
    body: [
      'A long-length partial is simply a partial rep in the stretched aspect of the lift. '
      + 'For example, if you are doing long-length partials on an assisted pull-up, you would '
      + 'come about half way up, doing partial reps in the bottom part of the pull-up.',
      'As another example, on a lying leg curl you would do partial reps in the bottom half of '
      + 'the curl, where the leg is more straight rather than more bent.',
      'More and more research is showing that the stretched/lengthened aspect of the lift is '
      + 'better for hypertrophy than the squeezed/shortened aspect. Long-length partials let you '
      + 'spend more time in the most hypertrophic part of the range of motion.',
    ],
    note:
      'When long-length partials are listed, they are performed on every rep of the LAST set only. '
      + 'On a 3 x 10-12, sets 1 and 2 use a full range of motion as normal; on set 3 you do '
      + 'long-length partials on all 10-12 reps. They are not being used here to extend a set '
      + 'beyond failure.',
  },
  {
    id: 'myo-reps',
    title: 'Myo-reps',
    summary: 'Extend a set past failure with short mini-rests.',
    body: [
      'Myo-reps are when you extend a set beyond failure by taking short mini-rests and cranking '
      + 'out a few extra reps in between them.',
      'They work because they let you squeeze out extra reps while the muscle is near full '
      + 'exhaustion. Reps close to the end of a set are more "effective" for muscle growth than '
      + 'reps toward the beginning, and myo-reps get you more of those effective reps.',
    ],
    steps: [
      'Do the earlier sets as normal.',
      'On the last set, hit your target reps and reach failure.',
      'Rest about 5 seconds.',
      'Do another 3-4 reps.',
      'Rest about 5 seconds again.',
      'Do another 3-4 reps.',
      'Repeat until you can no longer get at least 3 reps — then the set is finished.',
    ],
  },
  {
    id: 'dropsets',
    title: 'Dropsets',
    summary: 'Drop the weight and keep going at the end of a set.',
    body: [
      'A dropset is when you drop the weight back and perform more reps at the end of a set.',
      'This program uses small dropsets, not big ones. It is generally smarter to drop the weight '
      + 'back by a small amount so that you only get another few reps — that way all of the extra '
      + 'reps are actually effective, rather than wasting time and effort on reps far from failure.',
    ],
    steps: [
      'Finish your last set — say 10-12 reps with 60 lb.',
      'Immediately drop the weight back by 20-30% (here, to about 45 lb).',
      'Perform as many more reps as you can until you reach failure again with good form.',
    ],
    note: 'Only drop the weight back by 20-30%, then get straight back to work.',
  },
  {
    id: 'mechanical-dropsets',
    title: 'Mechanical Dropsets',
    summary: 'Change the technique instead of the weight.',
    body: [
      'A mechanical dropset is similar to a traditional dropset, except that instead of dropping '
      + 'the weight back you make the exercise easier by strategically modifying your technique. '
      + 'That modification lets you squeeze out some extra reps.',
      'For example, this program does a mechanical dropset on the Cable Reverse Flye for the rear '
      + 'delts by taking a step toward the cable machine at set rep intervals.',
    ],
  },
  {
    id: 'integrated-partials',
    title: 'Integrated Partials',
    summary: 'Alternate full reps and stretched partial reps within the set.',
    body: [
      'Integrated partials are a type of long-length partial where, instead of doing all of the '
      + 'reps in a set as partials, you integrate them throughout the set by alternating between '
      + 'full range-of-motion reps and long-length partial reps.',
      'By placing the partial reps in the middle of the set, you get any potential benefit of a '
      + 'full range of motion while spending roughly twice the time in the lengthened part of the '
      + 'range.',
    ],
    steps: [
      'Do 1 rep with a full range of motion.',
      'Do 1 rep with a partial range of motion — a half rep in the stretched aspect.',
      'Do 1 rep with a full range of motion.',
      'Do 1 rep with a partial range of motion.',
      'Repeat until you hit the target rep count.',
    ],
    note:
      'These are not used on every exercise because they can feel disruptive to the flow of a set. '
      + 'There is also no research on this method specifically — it is included as an experimental '
      + 'one with theoretical merit.',
  },
  {
    id: 'static-stretches',
    title: 'Static Stretches',
    summary: 'A 30-second held stretch after the last set.',
    body: [
      'Static stretching is holding a stretch in a fixed position. In this program you hold a '
      + 'specific stretch for a muscle after the last set of an exercise — for example, a 30 '
      + 'second stretch for each quad after finishing your leg press.',
      'Because the stretch only comes after the last set, it will not hurt your performance. '
      + 'Research suggests static stretching only tends to impair performance when held longer '
      + 'than 60 seconds, so the 30 second holds here are well within the safe zone.',
      'Research on inter-set stretching is mixed, and the effect is likely muscle specific. '
      + 'Studies showing a benefit found it in the quads and calves, which is where this program '
      + 'focuses. Lat stretches are included because they help people connect with their lats and '
      + 'improve the pump, and pec stretches because they can substantially increase blood flow '
      + 'to the pecs.',
    ],
  },
];

export const TECHNIQUE_SOURCE = 'The Hypertrophy Handbook';

/**
 * Maps a prescription string from the program onto an explanation.
 *
 * The program writes these with qualifiers — "Long-length Partials (on all reps
 * of the last set)", "Calf Static Stretch (30 sec hold)" — so this matches on
 * the distinguishing words. Order matters: "Mechanical Dropset" must be tested
 * before the plain "Dropset" it contains.
 */
export function explainTechnique(technique: string | null | undefined): Technique | null {
  if (!technique) return null;
  const t = technique.toLowerCase();
  if (t === 'n/a') return null;

  const byId = (id: string) => TECHNIQUES.find(x => x.id === id) ?? null;

  if (t.includes('mechanical dropset')) return byId('mechanical-dropsets');
  if (t.includes('integrated partial')) return byId('integrated-partials');
  if (t.includes('long-length partial')) return byId('long-length-partials');
  if (t.includes('myo-rep')) return byId('myo-reps');
  if (t.includes('dropset')) return byId('dropsets');
  if (t.includes('static stretch')) return byId('static-stretches');
  return null;
}

export const techniqueById = (id: string | undefined): Technique | null =>
  TECHNIQUES.find(t => t.id === id) ?? null;
